import { typesenseQueryBy } from '@shared/core/search';
import {
  TYPESENSE_MAX_CANDIDATES,
  TYPESENSE_MAX_PER_PAGE,
  toTypesenseBoostSort,
  toTypesenseFilter,
} from '@shared/core/search/dialects/typesense';
import { type LotTypesenseDoc, lotDocFromTypesense } from '@shared/core/search/entities/lotTypesense';
import { z } from 'zod';

import { withRequestTimeout } from './timeout';
import { isAuthStatus, SearchAuthError, type SearchTransport } from './types';

const responseSchema = z.object({
  results: z.array(
    z.object({
      hits: z.array(z.object({ document: z.unknown() })).optional(),
      code: z.number().optional(),
      error: z.string().optional(),
    })
  ),
});

const DOCUMENT_MAPPERS: Readonly<Record<string, (document: unknown) => unknown>> = {
  lot: (document) => lotDocFromTypesense(document as LotTypesenseDoc),
};

const parseOrigins = (endpoint: string): string[] =>
  endpoint
    .split(',')
    .map((entry) => entry.trim())
    .flatMap((entry) => {
      try {
        return [new URL(entry).origin];
      } catch {
        return [];
      }
    });

export function createTypesenseTransport(endpoint: string, key: string): SearchTransport {
  return {
    async search<TDoc>({
      descriptor,
      query,
      filter,
      hitsPerPage,
      boosts,
      signal,
    }: Parameters<SearchTransport['search']>[0]) {
      const origins = parseOrigins(endpoint);
      if (origins.length === 0) throw new Error('Typesense endpoint has no usable node');

      const typesense = descriptor.ranking.typesense;
      const filterBy = toTypesenseFilter(filter);
      const trimmed = query.trim();
      const boostSort = toTypesenseBoostSort(boosts ?? []);
      const sortBy = [boostSort === '' ? '' : `_text_match(buckets: 10):desc,${boostSort}`, typesense?.sortBy ?? '']
        .filter((clause) => clause !== '')
        .join(',');
      const mapDocument = DOCUMENT_MAPPERS[descriptor.entity];
      const body = JSON.stringify({
        searches: [
          {
            collection: descriptor.indexName.typesense,
            q: trimmed === '' ? '*' : trimmed,
            query_by: typesenseQueryBy(descriptor),
            per_page: Math.min(hitsPerPage, TYPESENSE_MAX_PER_PAGE),
            drop_tokens_threshold: 0,
            max_candidates: TYPESENSE_MAX_CANDIDATES,
            exclude_fields: 'embedding',
            ...(filterBy === '' ? {} : { filter_by: filterBy }),
            ...(sortBy === '' ? {} : { sort_by: sortBy }),
          },
        ],
      });

      let lastError: unknown = new Error('Typesense search failed');
      for (const origin of origins) {
        let response: Response;
        try {
          response = await fetch(`${origin}/multi_search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-TYPESENSE-API-KEY': key },
            body,
            signal: withRequestTimeout(signal),
          });
        } catch (error) {
          if (signal?.aborted) throw error;
          lastError = error;
          continue;
        }

        if (isAuthStatus(response.status)) throw new SearchAuthError(response.status);
        if (response.status >= 500) {
          lastError = new Error(`Typesense responded ${response.status}`);
          continue;
        }
        if (!response.ok) throw new Error(`Typesense responded ${response.status}`);

        const leg = responseSchema.parse(await response.json()).results[0];
        if (!leg) throw new Error('Typesense returned no result');
        if (leg.error !== undefined) {
          if (leg.code !== undefined && isAuthStatus(leg.code)) throw new SearchAuthError(leg.code);
          throw new Error(`Typesense search failed: ${leg.error}`);
        }
        return (leg.hits ?? []).map((hit) => (mapDocument ? mapDocument(hit.document) : hit.document) as TDoc);
      }

      throw lastError;
    },
  };
}
